import {
  Bleed,
  Box,
  BoxProps,
  Flex,
  HStack,
  LinkBox,
  LinkOverlay,
  NativeSelect,
  Separator,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import * as React from "react";
import {
  LuChevronLeft,
  LuChevronRight,
  LuChevronsLeft,
  LuChevronsRight,
} from "react-icons/lu";
import { Link as RouterLink } from "react-router-dom";

import { Button } from "./button";

export interface DataTableContext<T> {
  id?: keyof T;
  index: number;
  value: T;
}

export interface DataTableColumn<T> {
  id?: keyof T;
  name?: string;
  icon?: React.ReactElement;
  width?: number;
  align?: "start" | "center" | "end";
  cell?: (ctx: DataTableContext<T>) => React.ReactNode;
}

export interface DataTablePagination {
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  /**
   * Cursor-based feeds can only step one page at a time; hides the
   * jump-to-first/last controls that would request an unreachable page.
   */
  sequential?: boolean;
}

export interface DataTableProps<T> extends Omit<BoxProps, "columns"> {
  data: T[];
  columns: DataTableColumn<T>[];
  skeleton?: number;
  /**
   * CSS grid-template-columns string. When set, the table renders in the
   * ROAS 2.0 style (gray shell, white inset-ring content, grid rows).
   */
  template?: string;
  /** Render without the outer gray shell (e.g. inside an ActivityLog). */
  bare?: boolean;
  /** Minimum content width before horizontal scrolling kicks in. */
  minTableWidth?: string;
  onRowClick?: (value: T, index: number) => void;
  /**
   * Anchors a link overlay on each row's first cell, covering the whole
   * row, so rows can be opened in a new tab. Return null for rows that
   * shouldn't link anywhere.
   */
  rowHref?: (value: T, index: number) => string | null;
  pagination?: boolean | DataTablePagination;
}

const PAGE_SIZES = [10, 25, 50];
const JUSTIFY = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
} as const;

export default function DataTable<T>({
  bare,
  columns,
  data,
  minTableWidth,
  onRowClick,
  pagination,
  rowHref,
  skeleton,
  template,
  ...rootProps
}: DataTableProps<T>) {
  if (template !== undefined) {
    return (
      <GridDataTable
        {...{
          bare,
          columns,
          data,
          minTableWidth,
          onRowClick,
          pagination,
          rowHref,
          skeleton,
          template,
        }}
        {...rootProps}
      />
    );
  }
  return <LegacyDataTable {...{ columns, data, skeleton }} {...rootProps} />;
}

function GridDataTable<T>({
  bare,
  columns,
  data,
  minTableWidth,
  onRowClick,
  pagination,
  rowHref,
  skeleton,
  template,
  ...rootProps
}: DataTableProps<T> & { template: string }) {
  const opts: DataTablePagination =
    typeof pagination === "object" ? pagination : {};
  const controlled = opts.onPageChange !== undefined;
  const [ownPage, setOwnPage] = React.useState(0);
  const [ownPageSize, setOwnPageSize] = React.useState(opts.pageSize ?? 10);
  const pageSize = controlled ? (opts.pageSize ?? 10) : ownPageSize;
  const total = opts.total ?? data.length;
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  const page = Math.min(Math.max(0, opts.page ?? ownPage), lastPage);
  const canChangePageSize = !controlled || opts.onPageSizeChange !== undefined;

  const isShowingSkeleton = skeleton !== undefined && skeleton > 0;
  const displayData = React.useMemo(() => {
    if (isShowingSkeleton) {
      return Array.from({ length: skeleton }, (_, i) => ({
        id: `skeleton-${i}`,
      })) as unknown as T[];
    }
    if (pagination && !controlled) {
      return data.slice(page * pageSize, (page + 1) * pageSize);
    }
    return data;
  }, [
    controlled,
    data,
    isShowingSkeleton,
    page,
    pageSize,
    pagination,
    skeleton,
  ]);

  const setPage = (next: number) => {
    const clamped = Math.max(0, Math.min(lastPage, next));
    if (opts.onPageChange) {
      opts.onPageChange(clamped);
      return;
    }
    setOwnPage(clamped);
  };
  const setPageSize = (next: number) => {
    if (opts.onPageSizeChange) {
      opts.onPageSizeChange(next);
    } else {
      setOwnPageSize(next);
    }
    setPage(0);
  };

  const shown = Math.min((page + 1) * pageSize, total);
  const clickable = onRowClick !== undefined;

  const content = (
    <Box background="white" boxShadow="insetRing" overflow="hidden">
      <Box overflowX="auto">
        <Box minWidth={(minTableWidth as any) ?? undefined}>
          <Box
            alignItems="stretch"
            background="bg.subtle"
            display="grid"
            gridTemplateColumns={template as any}
            minHeight="12"
          >
            {columns.map((c, idx) => (
              <HStack
                key={idx}
                gap="1.5"
                justify={JUSTIFY[c.align ?? "start"]}
                paddingX="4"
                paddingY="3"
                textStyle="tableHeader"
                whiteSpace="nowrap"
              >
                {c.icon}
                {c.name}
              </HStack>
            ))}
          </Box>
          {displayData.map((d, rowIdx) => {
            const href = isShowingSkeleton
              ? null
              : (rowHref?.(d, rowIdx) ?? null);
            const interactive = clickable || href !== null;
            return (
              <LinkBox
                key={rowIdx}
                alignItems="stretch"
                background="white"
                borderColor="border.soft"
                borderTopWidth="1"
                cursor={interactive ? ("pointer" as any) : undefined}
                display="grid"
                gridTemplateColumns={template as any}
                onClick={
                  clickable && !isShowingSkeleton
                    ? () => onRowClick(d, rowIdx)
                    : undefined
                }
                _hover={interactive ? { background: "bg.hover" } : undefined}
              >
                {columns.map((c, idx) => {
                  const cell = isShowingSkeleton ? (
                    <Skeleton h="5" w="full" />
                  ) : c.cell ? (
                    c.cell({ id: c.id, index: rowIdx, value: d })
                  ) : (
                    c.id && <Text truncate>{String(d[c.id])}</Text>
                  );
                  return (
                    <Flex
                      key={idx}
                      align="center"
                      borderColor="border.soft"
                      borderLeftWidth={idx > 0 ? "1" : "0"}
                      color="fg"
                      fontSize="sm"
                      fontVariantNumeric="tabular-nums"
                      gap="1.5"
                      justify={JUSTIFY[c.align ?? "start"]}
                      lineHeight={"20px" as any}
                      minHeight={"62px" as any}
                      minWidth="0"
                      padding="4"
                    >
                      {idx === 0 && href ? (
                        <LinkOverlay asChild minWidth="0">
                          <RouterLink to={href}>{cell}</RouterLink>
                        </LinkOverlay>
                      ) : (
                        cell
                      )}
                    </Flex>
                  );
                })}
              </LinkBox>
            );
          })}
        </Box>
      </Box>
    </Box>
  );

  const footer = pagination ? (
    <Flex
      align="center"
      gap="4"
      height="16"
      justify="space-between"
      paddingBottom="4"
      paddingTop="3"
      paddingX="4"
    >
      <Text color="fg.muted" fontSize="sm">
        Showing: {isShowingSkeleton ? "…" : `${shown} of ${total}`}
      </Text>
      <HStack gap="2">
        {!opts.sequential && (
          <PageButton
            aria-label="First page"
            disabled={page === 0}
            onClick={() => setPage(0)}
          >
            <LuChevronsLeft />
          </PageButton>
        )}
        <PageButton
          aria-label="Previous page"
          disabled={page === 0}
          onClick={() => setPage(page - 1)}
        >
          <LuChevronLeft />
        </PageButton>
        <PageButton
          aria-label="Next page"
          disabled={page >= lastPage}
          onClick={() => setPage(page + 1)}
        >
          <LuChevronRight />
        </PageButton>
        {!opts.sequential && (
          <PageButton
            aria-label="Last page"
            disabled={page >= lastPage}
            onClick={() => setPage(lastPage)}
          >
            <LuChevronsRight />
          </PageButton>
        )}
      </HStack>
      {canChangePageSize && (
        <HStack color="fg" fontSize="sm" gap="2">
          <Text whiteSpace="nowrap">Rows per page</Text>
          <NativeSelect.Root size="sm" width="auto">
            <NativeSelect.Field
              background="white"
              borderWidth="0"
              borderRadius="l1"
              boxShadow="card"
              value={String(pageSize)}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </HStack>
      )}
    </Flex>
  ) : null;

  if (bare) {
    return (
      <>
        {content}
        {footer}
      </>
    );
  }
  return (
    <Box
      background="bg.subtle"
      borderRadius="l3"
      boxShadow="table"
      overflow="hidden"
      {...(rootProps as any)}
    >
      {content}
      {footer}
    </Box>
  );
}

function PageButton(props: React.ComponentProps<typeof Button>) {
  return (
    <Button
      variant="surface"
      size="sm"
      minWidth="8"
      paddingX="0"
      width="8"
      {...props}
    />
  );
}

function LegacyDataTable<T>({
  columns,
  data,
  skeleton,
  ...rootProps
}: DataTableProps<T>) {
  const ref = React.useRef(null);
  const [width, setWidth] = React.useState<number>(0);
  const columnWidthsSum = React.useMemo(
    () => columns.reduce((acc, c) => acc + (c.width ?? 0), 0),
    [columns],
  );
  const relativeColumnWidths = React.useMemo(
    () => columns.map((c) => ((c.width ?? 0) / columnWidthsSum) * width),
    [columns, columnWidthsSum, width],
  );
  const getColumnWidth = React.useCallback(
    (idx: number) => {
      const w = relativeColumnWidths[idx];
      if (w === 0) {
        return "auto";
      }
      return `${w}px`;
    },
    [relativeColumnWidths],
  );

  const displayData = React.useMemo(() => {
    if (skeleton !== undefined && skeleton > 0) {
      return Array.from({ length: skeleton }, (_, i) => ({
        id: `skeleton-${i}`,
      })) as unknown as T[];
    }
    return data;
  }, [data, skeleton]);

  const isShowingSkeleton = skeleton !== undefined && skeleton > 0;

  React.useLayoutEffect(() => {
    setWidth(ref.current.getBoundingClientRect().width - 120);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    function onResize() {
      setWidth(0);
      requestAnimationFrame(() => {
        setWidth(ref.current.getBoundingClientRect().width - 120);
      });
    }
  }, []);

  return (
    <Box
      bg="bg.muted"
      borderRadius="xs"
      borderWidth="1"
      {...(rootProps as any)}
    >
      <Stack bg="bg" borderBottomWidth="1" borderRadius="xs" overflow="hidden">
        <Bleed block="2" inline="2" mb="0">
          <HStack bg="gray.50" px="6" pt="4" pb="2" ref={ref} w="full">
            {columns.map((c, idx) => (
              <Box key={idx} textAlign={c.align} w={getColumnWidth(idx) as any}>
                {c.name && (
                  <Text
                    color="fg.muted"
                    fontSize="2xs"
                    fontWeight="medium"
                    letterSpacing="wide"
                    textTransform="uppercase"
                  >
                    {c.name}
                  </Text>
                )}
              </Box>
            ))}
          </HStack>
          <Separator />
        </Bleed>
        {displayData.map((d, idx) => (
          <React.Fragment key={idx}>
            <LinkBox>
              <HStack
                color="fg.muted"
                fontSize="sm"
                mt="-2"
                px="4"
                py="2"
                w="full"
                _hover={{ bg: "bg.subtle" }}
              >
                {columns.map((c, idx) => (
                  <Box
                    key={idx}
                    textAlign={c.align}
                    w={getColumnWidth(idx) as any}
                  >
                    {isShowingSkeleton ? (
                      <Skeleton w="full" h="5" />
                    ) : c.cell ? (
                      c.cell({ id: c.id, index: idx, value: d })
                    ) : (
                      c.id && <Text>{String(d[c.id])}</Text>
                    )}
                  </Box>
                ))}
              </HStack>
              {idx < displayData.length - 1 && (
                <Bleed inline="2">
                  <Separator />
                </Bleed>
              )}
            </LinkBox>
          </React.Fragment>
        ))}
      </Stack>
      <Box py="2"></Box>
    </Box>
  );
}
